import Checkbox from 'expo-checkbox';
import React, { useEffect, useState } from 'react';
import { Pressable, Platform, StyleProp, ViewStyle } from 'react-native';

import Colors from '../../constants/Colors';
import { StaticTexts } from '../../constants/StaticTexts';
import { useParent } from '../../hooks/useChild';
import useColorScheme from '../../hooks/useColorScheme';
import { useForm } from '../../hooks/useForm';
import { useFormArray } from '../../hooks/useFormArray';
import BirthdateInput from '../BirthdateInput';
import { PrimaryButton, UnderlinedButton } from '../Button';
import { Icon } from '../Icon';
import { PhoneNumberInput } from '../PhoneInput';
import { TransparentRow } from '../Row';
import SelectInput from '../SelectInput';
import { TextBodySmall, TextTag } from '../StyledText';
import { FormInput } from '../StyledTextInput';
import { TransparentView } from '../Themed';
import TimeInput from '../TimeInput';

type LeadFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  whenDoYouNeedCare: string;
  fixedCare: boolean;
  flexibleCare: boolean;
  weekendCare: boolean;
  eveningCare: boolean;
  childrenBirths: string[];
};

export type LeadFormFieldsRef = {
  setValues: (values: Partial<LeadFormValues>) => void;
};

type LeadFormFieldsProps = {
  wide?: boolean;
  onSubmit?: (values: LeadFormValues, selectedKidsIds: number[]) => void;
  fieldsConfig?: Partial<{
    tourTimes: boolean;
    specificCareTimes: boolean;
  }>;
};

const LeadFormFields = ({
  wide = false,
  fieldsConfig,
  onSubmit,
}: LeadFormFieldsProps) => {
  const parent = useParent();
  const { state, dispatch, getSerializedObject } = useForm<LeadFormValues>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    whenDoYouNeedCare: '',
  });

  const [childrenBirths, setChildrenBirths] = useState<string[]>([]);

  const [kindsSelected, setKindsSelected] = useState({
    fixed: false,
    flexible: false,
    weekend: false,
    evening: false,
  });

  const [tourTimes, setTourTimes] = useState<
    {
      date: string;
      time: string;
    }[]
  >([{ date: '', time: '' }]);

  const [careTimes, setCareTimes] = useState<CareTimes>({
    days: new Set(),
    timeFrom: '9:00 AM',
    timeTo: '5:00 PM',
  });

  const onSpecificCareTimesChange = (value: Partial<CareTimes>) => {
    setCareTimes((c) => ({ ...c, ...value }));
  };

  const setValues = (values: Partial<LeadFormValues>) => {
    dispatch({ type: 'setValues', values });
  };

  const [selectedKidsIds, setSelectedKidsIds] = useState<number[]>([]);
  useEffect(() => {
    if (parent.id) {
      setValues({
        firstName: parent.firstName ?? '',
        lastName: parent.lastName ?? '',
        email: parent.email ?? '',
        phoneNumber: parent.telephone ?? '',
      });
      if (parent.children) {
        setSelectedKidsIds(parent.children.map((child) => child.id));
      }
    } else {
      setSelectedKidsIds([]);
    }
  }, [parent]);

  const updateKindSelection = (
    type: 'fixed' | 'flexible' | 'weekend' | 'evening',
    value: boolean
  ) => setKindsSelected((s) => ({ ...s, [type]: value }));

  const submit = () => {
    const finalFormValues = {
      ...getSerializedObject(),
      childrenBirths,
    };
    onSubmit?.(finalFormValues, selectedKidsIds);
  };

  return (
    <>
      <TextBodySmall style={{ marginBottom: 5 }}>
        Your information
      </TextBodySmall>
      <TransparentRow style={{ gap: 20 }}>
        <FormInput
          placeholder="First Name"
          style={{ flex: 1 }}
          value={state.values.firstName}
          onChangeText={(value) =>
            dispatch({ type: 'field', field: 'firstName', value })
          }
        />
        <FormInput
          placeholder="Last Name"
          style={{ flex: 1 }}
          value={state.values.lastName}
          onChangeText={(value) =>
            dispatch({ type: 'field', field: 'lastName', value })
          }
        />
      </TransparentRow>
      <TransparentRow
        style={{
          flexDirection: wide ? 'row' : 'column',
          width: wide ? '100%' : 'auto',
          gap: wide ? 20 : 0,
        }}
      >
        <FormInput
          placeholder="Email"
          value={state.values.email}
          onChangeText={(value) =>
            dispatch({ type: 'field', field: 'email', value })
          }
          style={{ flex: 1 }}
        />
        <PhoneNumberInput
          placeholder="Phone Number"
          value={state.values.phoneNumber}
          onChangeText={(value) =>
            dispatch({ type: 'field', field: 'phoneNumber', value })
          }
          style={{ flex: 1 }}
          testID="phone-input"
        />
      </TransparentRow>
      {fieldsConfig?.tourTimes ? (
        <ToursDatesInput value={tourTimes} onChange={setTourTimes} />
      ) : null}
      {fieldsConfig?.specificCareTimes ? null : (
        <SelectInput
          label={'When do you need care?'}
          labelStyle={{ marginBottom: 5 }}
          value={state.values.whenDoYouNeedCare}
          placeholder="Please make a selection"
          options={[
            'As soon as possible',
            'In a few weeks',
            'In a few months',
            'Not sure',
          ]}
          onChange={(value) =>
            dispatch({ type: 'field', field: 'whenDoYouNeedCare', value })
          }
        />
      )}
      <TextBodySmall>
        What kind of care do you need? (Check all that apply.)
      </TextBodySmall>
      <TransparentRow
        style={{
          justifyContent: 'flex-start',
          marginTop: 10,
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: 20,
        }}
      >
        <CheckboxInput
          selected={kindsSelected.fixed}
          label={StaticTexts.searchLeadForm.kindOfCare.fixed}
          testID={'fixed-care'}
          onChange={(value) => updateKindSelection('fixed', value)}
        />
        <CheckboxInput
          selected={kindsSelected.flexible}
          label={StaticTexts.searchLeadForm.kindOfCare.flexible}
          testID={'flexible-care'}
          onChange={(value) => updateKindSelection('flexible', value)}
        />
        <CheckboxInput
          selected={kindsSelected.weekend}
          label={StaticTexts.searchLeadForm.kindOfCare.weekend}
          testID={'weekend-care'}
          onChange={(value) => updateKindSelection('weekend', value)}
        />
        <CheckboxInput
          selected={kindsSelected.evening}
          label={StaticTexts.searchLeadForm.kindOfCare.evening}
          testID={'evening-care'}
          onChange={(value) => updateKindSelection('evening', value)}
        />
      </TransparentRow>
      {fieldsConfig?.specificCareTimes ? (
        <SpecificCareTimesFields
          value={careTimes}
          onChange={onSpecificCareTimesChange}
        />
      ) : null}
      <TransparentView style={{ marginBottom: 20 }}>
        {parent.id ? (
          <ChildsList
            childsInfo={parent.children}
            {...{ selectedKidsIds, setSelectedKidsIds }}
          />
        ) : (
          <ChildrensBirths
            value={childrenBirths}
            onChange={setChildrenBirths}
          />
        )}
      </TransparentView>
      <PrimaryButton title="Submit" onPress={submit} testID="submit-button" />
    </>
  );
};

function ToursDatesInput({
  value,
  onChange,
}: {
  value: { date: string; time: string }[];
  onChange: (value: { date: string; time: string }[]) => void;
}) {
  const theme = useColorScheme();
  return (
    <TransparentView style={{ marginBottom: 20 }}>
      <TextBodySmall style={{ marginBottom: 5 }}>
        Preferred date(s) and time(s) for a tour
      </TextBodySmall>
      {value.map((v, index) => (
        <TransparentView key={`tour-date-item-${index}`}>
          {value.length > 1 ? (
            <Pressable
              onPress={() => onChange(value.filter((_, i) => i !== index))}
              style={{ alignSelf: 'flex-end' }}
            >
              <Icon
                name="close"
                color={Colors[theme].text}
                style={{ width: 15, height: 15 }}
              />
            </Pressable>
          ) : null}
          <TransparentRow style={{ marginBottom: 15, gap: 20 }}>
            <BirthdateInput
              testID={`tour-date-${index}`}
              value={v.date}
              placeholder="Date"
              onChange={function (value: string): void {
                console.log('onChange', value);
              }}
              onCalendarChange={function (value: string): void {
                console.log('onCalendarChange', value);
              }}
              wrapperStyle={{ flex: 1, marginBottom: 0 }}
            />
            <TimeInput
              style={{ flex: 1 }}
              label="Time"
              value={v.time}
              onChange={(value) => {
                console.log('onChange', value);
              }}
            />
          </TransparentRow>
        </TransparentView>
      ))}
      <UnderlinedButton
        style={{ alignSelf: 'flex-start' }}
        testID="add-tour-date-button"
        title="+ Add a date"
        onPress={() => onChange([...value, { date: '', time: '' }])}
      />
    </TransparentView>
  );
}

type CareTimes = {
  dateFrom?: string;
  dateTo?: string;
  days: Set<'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun'>;
  timeFrom?: string;
  timeTo?: string;
};

function SpecificCareTimesFields({
  value,
  onChange,
  extended = false,
}: {
  value: CareTimes;
  onChange: (value: Partial<CareTimes>) => void;
  extended?: boolean;
}) {
  return (
    <TransparentView style={{ marginBottom: 20 }}>
      <TextBodySmall style={{ marginBottom: 5 }}>
        When do you need care? (Check all that apply.)
      </TextBodySmall>
      {extended ? (
        <TransparentRow>
          <BirthdateInput
            testID="specific-care-date-from"
            value={value.dateFrom}
            onChange={(value) => onChange({ dateFrom: value })}
            onCalendarChange={(value) => onChange({ dateFrom: value })}
            wrapperStyle={{ flex: 1, marginBottom: 0 }}
          />
          <BirthdateInput
            testID="specific-care-date-to"
            value={value.dateTo}
            onChange={(value) => onChange({ dateTo: value })}
            onCalendarChange={(value) => onChange({ dateTo: value })}
            wrapperStyle={{ flex: 1, marginBottom: 0 }}
          />
        </TransparentRow>
      ) : null}
      <TransparentRow
        style={{
          flexWrap: 'wrap',
          gap: 10,
          justifyContent: 'flex-start',
          marginBottom: 20,
        }}
      >
        {(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const).map(
          (day) => (
            <CheckboxInput
              selected={value.days?.has(day)}
              label={day}
              testID={`specific-care-${day}`}
              onChange={(v) => {
                const next = new Set(value.days);
                if (v) {
                  next.add(day);
                } else {
                  next.delete(day);
                }
                onChange({ days: next });
              }}
              key={`specific-care-${day}`}
            />
          )
        )}
      </TransparentRow>
      <TextBodySmall style={{ marginBottom: 5 }}>
        Desired dropoff and pickup times
      </TextBodySmall>
      <TransparentRow style={{ gap: 20 }}>
        <TimeInput
          style={{ flex: 1 }}
          label="Dropoff"
          value={value.timeFrom ?? ''}
          onChange={(value) => onChange({ timeFrom: value })}
        />
        <TimeInput
          style={{ flex: 1 }}
          label="Pickup"
          value={value.timeTo ?? ''}
          onChange={(value) => onChange({ timeTo: value })}
        />
      </TransparentRow>
    </TransparentView>
  );
}

function ChildsList({
  childsInfo,
  selectedKidsIds,
  setSelectedKidsIds,
}: {
  childsInfo: ReturnType<typeof useParent>['children'];
  selectedKidsIds: number[];
  setSelectedKidsIds: (ids: number[]) => void;
}) {
  const theme = useColorScheme();
  if (!childsInfo) return null;
  return (
    <>
      <TextBodySmall style={{ marginBottom: 10, marginTop: 10 }}>
        Child(ren)'s birthdates:
      </TextBodySmall>

      <TransparentView>
        {childsInfo.map((child) => (
          <TransparentRow
            style={{
              gap: 10,
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
            key={child.id}
          >
            <Checkbox
              value={selectedKidsIds.includes(child.id)}
              color={Colors[theme].accent}
              onValueChange={(value) => {
                if (value) {
                  setSelectedKidsIds([...selectedKidsIds, child.id]);
                } else {
                  setSelectedKidsIds(
                    selectedKidsIds.filter((id) => id !== child.id)
                  );
                }
              }}
            />
            <TransparentView
              style={[
                { gap: 2, marginBottom: 10 },
                !selectedKidsIds.includes(child.id) && {
                  opacity: 0.5,
                },
              ]}
            >
              <TextBodySmall>{child.firstName}</TextBodySmall>
              <TextTag>{child.birthdate}</TextTag>
            </TransparentView>
          </TransparentRow>
        ))}
      </TransparentView>
    </>
  );
}

export default LeadFormFields;

function CheckboxInput({
  testID,
  selected,
  onChange,
  label,
  style,
}: {
  testID: string;
  label: string;
  selected: boolean;
  onChange: (value: boolean) => void;
  style?: StyleProp<ViewStyle>;
}) {
  const theme = useColorScheme();
  return (
    <TransparentRow style={[{ justifyContent: 'flex-start' }, style]}>
      <Checkbox
        style={{
          height: 24,
          width: 24,
          marginRight: 5,
        }}
        testID={testID}
        onValueChange={() => onChange(!selected)}
        color={Colors[theme].accent}
        value={selected}
      />
      <TextBodySmall>{label}</TextBodySmall>
    </TransparentRow>
  );
}

function ChildrensBirths({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const theme = useColorScheme();
  const { state, dispatch } = useFormArray<{
    id: undefined;
    birthdate: string;
  }>(
    value.length > 0
      ? value.map((birthdate) => ({ id: undefined, birthdate }))
      : [{ id: undefined, birthdate: '' }]
  );

  useEffect(() => {
    onChange(state.values.map((child) => child.birthdate));
  }, [state.values]);

  return (
    <>
      <TextBodySmall
        style={
          state.values.length === 1
            ? {
                marginBottom: 15,
              }
            : {}
        }
      >
        Child(ren)'s Dates of Birth
      </TextBodySmall>
      {state.values.map((child, index) => (
        <React.Fragment key={`child-birthdate-${index}`}>
          {state.values.length > 1 ? (
            <Pressable
              style={{ alignSelf: 'flex-end' }}
              onPress={() => dispatch({ type: 'remove', index })}
              testID={`remove-child-${index}`}
            >
              <Icon
                name="close"
                color={Colors[theme].text}
                style={{
                  width: 15,
                  height: 15,
                  ...Platform.select({
                    native: {
                      width: 20,
                      height: 20,
                    },
                  }),
                }}
              />
            </Pressable>
          ) : null}
          <BirthdateInput
            testID={`child-birthdate-input`}
            value={child.birthdate}
            placeholder=""
            onChange={(value) =>
              dispatch({
                type: 'entryField',
                field: 'birthdate',
                value,
                index,
              })
            }
            onCalendarChange={(value) =>
              dispatch({
                type: 'entryField',
                field: 'birthdate',
                value,
                index,
              })
            }
          />
        </React.Fragment>
      ))}

      <UnderlinedButton
        style={{ alignSelf: 'flex-start' }}
        testID="add-child-button"
        title="+ Add a child"
        onPress={() => dispatch({ type: 'add' })}
      />
    </>
  );
}
